import { useMemo } from 'react'
import type {
  HistoryEvent,
  PermitEvent,
  TransactionEvent,
  ListingEvent,
} from '@popety-io/ui'

// Helper to safely convert date to ISO string
const toISOString = (date: Date | string | undefined): string => {
  if (!date) return ''
  if (typeof date === 'string') return date
  return date.toISOString()
}

/**
 * Transform a raw transaction to HistoryTab TransactionEvent format
 */
const transformTransaction = (transaction: any): TransactionEvent => {
  return {
    ...transaction,
    id: transaction.id || transaction.transaction_id,
    date:
      transaction.date ||
      transaction.transaction_date ||
      transaction.publication_date ||
      '',
    type: 'transaction',
    transaction_id: transaction.transaction_id || String(transaction.id),
    official_id: transaction.official_id,
    transaction_date: transaction.date || transaction.transaction_date,
    transaction_total_price:
      transaction.transaction_total_price || transaction.price || 0,
    transaction_step_total_price: transaction.transaction_step_total_price,
    transaction_step_name:
      transaction.transaction_step_name || transaction.reason,
    transaction_step_text:
      transaction.transaction_step_text ||
      transaction.details ||
      transaction.description,
    buyers_count: transaction.buyers_count || transaction.buyers_nb || 0,
    sellers_count: transaction.sellers_count || transaction.sellers_nb || 0,
    properties_count:
      transaction.properties_count ||
      transaction.transaction_properties?.length ||
      0,
    transaction_participants: transaction.transaction_participants || [],
    transaction_properties: transaction.transaction_properties || [],
  }
}

/**
 * Transform a raw listing to HistoryTab ListingEvent format
 */
const transformListing = (listing: any): ListingEvent => {
  // Determine type based on deal_type or category
  const type =
    listing.deal_type === 'rent' ||
    listing.category === 'rental' ||
    listing.deal_type === 'rental'
      ? 'rental'
      : 'sale'

  return {
    id: listing.id || listing.listing_id,
    date:
      listing.listing_timestamp ||
      listing.listing_date ||
      listing.create_timestamp ||
      '',
    type,
    price: listing.price || 0,
    pricePerSqm: listing.price_per_square_meter,
    address: listing.address,
    propertyType: listing.property_type,
    propertyCategory: listing.property_category,
    area:
      listing.living_area ||
      listing.land_area ||
      listing.surface ||
      listing.area,
    landArea: listing.land_area,
    rooms: listing.rooms_nb || listing.rooms,
    bathrooms: listing.bathroom_nb,
    floor: listing.floor,
    constructionYear: listing.construction_year,
    renovationYear: listing.renovation_year,
    description: listing.description,
    status: listing.ad_status ? 'Active' : 'Inactive',
    onlineDays: listing.listing_duration,
    imageUrls: listing.urls_image,
    brokerName: listing.broker_info?.contact_names?.[0]?.name,
    brokersInfo: listing.broker_info,
    availableDate: listing.active_listing_timestamp,
    features: listing.features,
    furnished: listing.features?.includes('Furnished'),
    petsAllowed: listing.features?.includes('Pets allowed'),
    parking: listing.features?.includes('Parking'),
    balcony: listing.features?.includes('Balcony'),
    terrace: listing.features?.includes('Terrace'),
    garden: listing.features?.includes('Garden'),
    garage: listing.features?.includes('Garage'),
    elevator: listing.features?.includes('Elevator'),
    energyLabel: listing.energy_label,
    heatingType: listing.heating_type,
  }
}

/**
 * Transform a raw building permit to HistoryTab PermitEvent format
 */
const transformBuildingPermit = (permit: any): PermitEvent => {
  return {
    ...permit,
    id: permit.id || permit.building_permit_id,
    date:
      toISOString(permit.inquiry_start_date) ||
      toISOString(permit.inquiry_last_date) ||
      permit.date ||
      '',
    type: 'permit',
    workDescription: permit.description || 'Building permit',
    permitNumber:
      permit.primary_official_id ||
      permit.secondary_official_id ||
      permit.fao_id,
    workType: permit.work_type_official || permit.publication_type_name,
    developmentPotential: permit.development_classification_llm,
    projectArea: permit.lands?.[0]?.area || permit.project_area,
    classification: permit.regbl_classification_general,
    inquiryStartDate: toISOString(permit.inquiry_start_date),
    inquiryLastDate: toISOString(permit.inquiry_last_date),
    participantsNumber: permit.participants?.length,
  }
}

/**
 * Extract history events from value_edited.history structure
 * The orchestrator saves history as: { listings: [], transactions: [], buildingPermits: [] }
 */
const extractHistoryFromValueEdited = (history: any): HistoryEvent[] => {
  const events: HistoryEvent[] = []

  // Transform transactions
  if (Array.isArray(history?.transactions)) {
    history.transactions.forEach((transaction: any) => {
      events.push(transformTransaction(transaction))
    })
  }

  // Transform listings
  if (Array.isArray(history?.listings)) {
    history.listings.forEach((listing: any) => {
      events.push(transformListing(listing))
    })
  }

  // Transform building permits
  if (Array.isArray(history?.buildingPermits)) {
    history.buildingPermits.forEach((permit: any) => {
      events.push(transformBuildingPermit(permit))
    })
  }

  return events
}

/**
 * Extract history events from partial estimate data (lands/buildings)
 * This is used as fallback when pre-fetched history is not available
 */
const extractHistoryFromEstimate = (estimateData: any): HistoryEvent[] => {
  const events: HistoryEvent[] = []
  const seenIds = new Set<string>()

  // Helper to add event if not already seen
  const addEvent = (event: HistoryEvent) => {
    const key = `${event.type}-${event.id}`
    if (!seenIds.has(key)) {
      seenIds.add(key)
      events.push(event)
    }
  }

  // Extract from lands
  const lands = estimateData?.value?.lands
  if (Array.isArray(lands)) {
    lands.forEach((land: any) => {
      // Transactions
      if (Array.isArray(land.transactions)) {
        land.transactions.forEach((transaction: any) => {
          addEvent(transformTransaction(transaction))
        })
      }

      // Listings
      if (Array.isArray(land.listings)) {
        land.listings.forEach((listing: any) => {
          addEvent(transformListing(listing))
        })
      }

      // Building permits
      if (Array.isArray(land.construction_permits)) {
        land.construction_permits.forEach((permit: any) => {
          addEvent(transformBuildingPermit(permit))
        })
      }
    })
  }

  // Extract from buildings
  const buildings = estimateData?.value?.buildings
  if (Array.isArray(buildings)) {
    buildings.forEach((building: any) => {
      // Transactions from building lands
      if (Array.isArray(building.transactions)) {
        building.transactions.forEach((transaction: any) => {
          addEvent(transformTransaction(transaction))
        })
      }

      // Listings
      if (Array.isArray(building.listings)) {
        building.listings.forEach((listing: any) => {
          addEvent(transformListing(listing))
        })
      }

      // Building permits
      if (Array.isArray(building.construction_permits)) {
        building.construction_permits.forEach((permit: any) => {
          addEvent(transformBuildingPermit(permit))
        })
      }
    })
  }

  return events
}

/**
 * Hook to get history data for the public estimate (read-only)
 * Uses pre-fetched data from value_edited.history or extracts from estimate data
 *
 * The value_edited.history structure from orchestrator:
 * {
 *   listings: [...full listing objects],
 *   transactions: [...full transaction objects],
 *   buildingPermits: [...full building permit objects]
 * }
 */
export const useEstimateHistoryData = (estimateData: any) => {
  const events = useMemo<HistoryEvent[]>(() => {
    // First, check for pre-fetched full history data in value_edited.history
    // This is set by the estimate orchestrator when sharing
    const history = estimateData?.value_edited?.history
    if (history && typeof history === 'object') {
      const hasData =
        (Array.isArray(history.listings) && history.listings.length > 0) ||
        (Array.isArray(history.transactions) &&
          history.transactions.length > 0) ||
        (Array.isArray(history.buildingPermits) &&
          history.buildingPermits.length > 0)

      if (hasData) {
        return extractHistoryFromValueEdited(history)
      }
    }

    // Fall back to extracting partial data from estimate (minimal data)
    const extractedEvents = extractHistoryFromEstimate(estimateData)
    if (extractedEvents.length > 0) {
      return extractedEvents
    }

    return []
  }, [estimateData])

  // Sort events by date (newest first)
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const dateA = new Date(a.date || 0).getTime()
      const dateB = new Date(b.date || 0).getTime()
      return dateB - dateA
    })
  }, [events])

  return {
    events: sortedEvents,
    loading: !estimateData,
    error: null,
  }
}
